import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { apiJson, apiFetch } from "../../api/http";
import { getStripe } from "../../billing/stripeClient";

/* global Office */

type Entitlement = {
  enforced: boolean;
  active: boolean;
  status: string;
  plan: string | null;
};

type GateState =
  | { kind: "loading" }
  | { kind: "allow" }
  | { kind: "blocked" }
  | { kind: "activating" };

/**
 * On Word for iPad / iPhone / Android, AppSource policy 1120.2 forbids ANY
 * payment UI, pricing, or purchase links inside the add-in — so mobile shows a
 * neutral message and NEVER the checkout.
 */
function isMobileOfficeHost(): boolean {
  try {
    const p = Office?.context?.platform;
    return p === Office.PlatformType.iOS || p === Office.PlatformType.Android;
  } catch {
    return false;
  }
}

async function fetchEntitlement(): Promise<Entitlement> {
  return apiJson<Entitlement>("/api/me/entitlement");
}

export default function EntitlementGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GateState>({ kind: "loading" });
  const stripePromise = useRef(getStripe());

  const check = useCallback(async () => {
    try {
      const ent = await fetchEntitlement();
      if (!ent.enforced || ent.active) return { allow: true };
      return { allow: false };
    } catch {
      // Fail open — the backend still enforces on every endpoint.
      return { allow: true };
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await check();
      if (!cancelled) setState(r.allow ? { kind: "allow" } : { kind: "blocked" });
    })();
    return () => {
      cancelled = true;
    };
  }, [check]);

  // Embedded Checkout requires a stable fetchClientSecret reference.
  const fetchClientSecret = useCallback(async (): Promise<string> => {
    const resp = await apiFetch("/api/billing/create-checkout-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ embedded: true }),
    });
    const data = (await resp.json()) as { clientSecret?: string };
    if (!data.clientSecret) throw new Error("Could not start checkout.");
    return data.clientSecret;
  }, []);

  // After payment completes in-pane, poll entitlement until the webhook marks
  // the subscription active, then unlock — no navigation/redirect.
  const onComplete = useCallback(async () => {
    setState({ kind: "activating" });
    for (let i = 0; i < 12; i++) {
      const r = await check();
      if (r.allow) {
        setState({ kind: "allow" });
        return;
      }
      await new Promise((res) => setTimeout(res, 2000));
    }
    // Still not active (webhook delay) — allow through; the backend re-checks.
    setState({ kind: "allow" });
  }, [check]);

  if (state.kind === "loading" || state.kind === "activating") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-3">
        <span
          className="inline-block w-6 h-6 rounded-full border-2 border-brand-200 border-t-brand-500 animate-spin"
          aria-label="Loading"
        />
        {state.kind === "activating" && (
          <p className="text-sm text-gray-600">Activating your subscription…</p>
        )}
      </div>
    );
  }

  if (state.kind === "allow") return <>{children}</>;

  // Blocked. Mobile → commerce-free message (policy 1120.2).
  if (isMobileOfficeHost()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6 py-10">
        <div className="w-full max-w-xs text-center">
          <h1 className="text-lg font-semibold text-gray-900">ESI Flow</h1>
          <p className="text-sm text-gray-600 mt-2 leading-6">
            This account doesn&apos;t have ESI Flow access yet. Please sign in on
            Word for Windows, Mac, or the web to get started.
          </p>
        </div>
      </div>
    );
  }

  // Desktop / web → full checkout INSIDE the task pane (no redirect).
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-5 pt-6 pb-3 text-center border-b border-gray-200 bg-white">
        <h1 className="text-lg font-semibold text-gray-900 tracking-tight">
          Activate ESI Flow
        </h1>
        <p className="text-sm text-gray-600 mt-1 leading-6">
          Subscribe to unlock clause analysis, jurisdiction checks, talking
          points, and AI assistance.
        </p>
      </div>
      <div className="p-3">
        <EmbeddedCheckoutProvider
          stripe={stripePromise.current}
          options={{ fetchClientSecret, onComplete }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    </div>
  );
}
